<template lang="pug">
    .establishments-categories
        v-dialog(
            v-model="removeDialog"
            persistent
            width="400px"
        )
            v-card
                v-card-title
                    span.title.mx-auto {{$vuetify.lang.t('$vuetify.pages.Delivery.Categories.deleting')}}
                v-card-text.pb-1.text-center
                    span {{$vuetify.lang.t('$vuetify.pages.Delivery.Categories.areYouSureToDelete')}}
                v-card-actions.d-flex.justify-center
                    v-btn(@click="closeRemoveDialog" text) {{$vuetify.lang.t('$vuetify.buttons.cancel')}}
                    v-btn.white--text(@click="remove" color="red") {{$vuetify.lang.t('$vuetify.buttons.remove')}}
        v-dialog(
            v-model="editDialog"
            v-if="editDialog"
            width="800px"
            persistent
        )
            v-card.px-3
                v-card-title.mb-7
                    span.headline.mx-auto {{ this.$vuetify.lang.t(`$vuetify.pages.EstablishmentsCategories.table.${isEditMode ? 'edit' : 'create'}`) }}
                .edit
                    v-locale(controlled v-model="editingLocale")
                    .d-flex.justify-space-between.mb-4.mt-3
                        v-text-field.mr-5(
                            v-model="category[localizeField('name', editingLocale)]"
                            :label="this.$vuetify.lang.t('$vuetify.pages.EstablishmentsCategories.table.name') + ' *'"
                            :counter="255"
                            maxLength="255"
                            outlined
                        )
                        v-checkbox.mt-3.align-self-start(
                            :label="this.$vuetify.lang.t('$vuetify.pages.EstablishmentsCategories.table.enabled')"
                            v-model="category.enabled"
                            color="secondary"
                            hide-details
                        )
                    v-image-uploader(
                        :width="302"
                        :height="160"
                        :initial-image="category.imgUrl"
                        :input-label="$vuetify.lang.t('$vuetify.pages.Delivery.photo')",
                        @choose-image="onChooseImage"
                    )
                v-card-actions.d-flex.justify-end.buttons
                    v-btn.white--text(@click="closeEditDialog" color="red") {{$vuetify.lang.t('$vuetify.buttons.cancel')}}
                    v-btn(
                        :disabled="!isSaveBtnEnabled"
                        :loading="requestProcessing"
                        @click="saveCategory"
                        color="secondary"
                    ) {{$vuetify.lang.t(`$vuetify.buttons.${isEditMode ? 'save' : 'create'}`)}}
        .v-card.elevation-5.pa-4.mb-5
            v-card-text.d-flex.justify-center
                span.headline {{ $vuetify.lang.t('$vuetify.filters') }}
            v-card-text.px-0
                v-row.d-flex.justify-center
                    v-col.py-0(cols="12" sm="12")
                        v-text-field(
                            :label="$vuetify.lang.t('$vuetify.pages.Brands.filters.query')"
                            v-model="filters.query"
                            @blur="loadItems()"
                            clearable
                            hide-details
                            outlined
                        )
        .table-wrapper
            v-data-table.elevation-5.data-table(
                :headers="headers"
                :items="items"
                :items-per-page="200"
                :mobile-breakpoint="300"
                hide-default-footer
            )
                template(v-slot:item.position="{ item }")
                    span {{ item.order }}
                template(v-slot:item.name="{ item }")
                    span {{ item.name }}
                template(v-slot:item.image="{ item }")
                    span {{ item.imgUrl | ellipsisInfo }}
                template(v-slot:item.enabled="{ item }")
                    span(:class="item.enabled ? 'active' : 'inactive'") {{ $vuetify.lang.t('$vuetify.pages.Delivery.Categories.visibility.' + (item.enabled ? 'shown' : 'hidden')) }}
                template(v-slot:item.createdAt="{ item }")
                    span.no-wrap {{ item.createdAt | timeAndDate }}
                template(v-slot:item.updatedAt="{ item }")
                    span.no-wrap {{item.updatedAt | timeAndDate }}
                template(v-slot:item.order="{ item }")
                    v-btn(@click="lowerCategory(item.id)" color="black" icon :disabled="item.order >= pagination.count")
                        v-icon mdi-chevron-down
                    v-btn(@click="raiseCategory(item.id)" color="black" icon :disabled="item.order <= 1")
                        v-icon mdi-chevron-up
                template(v-slot:item.actions="{ item }")
                    v-btn(@click="openEditDialog(item)" icon)
                        v-icon edit
                    v-btn(@click="openRemoveDialog(item.id)" color="red" icon :disabled="!!Number(item.establishmentCnt)")
                        v-icon delete
        v-pagination(
            v-if="pagination.totalPages > 1"
            :disabled="requestProcessing"
            :value="pagination.page"
            :length="pagination.totalPages"
            @input="onPageChange($event)"
            :total-visible="8"
            color="black"
        )
        v-btn.mx-2.add-btn(
            @click="openEditDialog"
            fab dark
        )
            v-icon(dark) mdi-plus
</template>

<script>
import request from '@/utils/request';
import { getFormData } from '@/utils/helpers';

import { mapState, mapGetters, mapActions } from 'vuex';
import {localizeField, multilingualFieldKeys, multilingualFields} from "@/helpers/locales";

const vImageUploader = () => import('@/components/image-uploader');

const categoryInitialState = () => ({
    ...multilingualFields(['name']),
    enabled: false,
    image: null,
    imgUrl: null,
});

export default {
    name: 'EstablishmentsCategories',
    components: {
        vImageUploader,
    },
    data: () => ({
        isLoading: false,
        editDialog: false,
        removeDialog: false,
        editingLocale: 'uk',
        idToRemove: null,
        category: categoryInitialState(),
        requestProcessing: false,
        filters: {
            query: null
        }
    }),
    setup() {
      return {
        localizeField
      };
    },
    computed: {
        ...mapGetters(['roleName']),
        ...mapState({
            items: state => state.establishmentsCategories.items,
            pagination: state => state.establishmentsCategories.pagination
        }),
        headers() {
            const titles = ['position', ...multilingualFieldKeys(['name']), /*'image',*/ 'enabled', 'createdAt', 'updatedAt', 'order', 'actions'];
            return titles.map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.EstablishmentsCategories.table.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        isEditMode() {
          return this.editDialog && this.category.id !== undefined;
        },
        isSaveBtnEnabled() {
            const requiredFields = [];
            if (!this.isEditMode) {
                requiredFields.push('image');
            }
            return requiredFields.every((field) => !!this.category[field]) && multilingualFieldKeys(['name']).some((field) => !!this.category[field]);
        }
    },
    created() {
        this.loadItems(this.pagination.page);
    },
    methods: {
        ...mapActions({
            getItems: 'establishmentsCategories/getItems',
            lowerCategoryRequest: 'establishmentsCategories/lowerCategory',
            raiseCategoryRequest: 'establishmentsCategories/raiseCategory',
        }),
        loadItems(page = 1) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.getItems(Object.assign({}, {
                page,
                ...this.filters
            })).then(() => {
                this.$nextTick(() => this.requestProcessing = false);
            });
        },
        lowerCategory(id) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.lowerCategoryRequest(id).then(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                    this.loadItems(this.pagination.page);
                });
            });
        },
        raiseCategory(id) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.raiseCategoryRequest(id).then(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                    this.loadItems(this.pagination.page);
                });
            });
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.loadItems(page);
            }
        },
        onChooseImage(file) {
            this.category.image = file;
        },
        openEditDialog(item) {
            this.category = { ...this.category, ...(item || {}) };
            this.editDialog = true;
        },
        async saveCategory() {
            if (this.requestProcessing) {
                return;
            }

            if (this.category.image && this.category.image.size > 5*1000*1000) {
              return this.$store.dispatch('alert/addMessage', {
                message: `imageIsTooLarge`,
                time: 2000
              });
            }

            const { id } = this.category;

            const method = id ? 'put' : 'post';
            const url = id ? `/delivery/category/${id}` : '/delivery/category';

            const ignoredKeys = ['createdAt', 'updatedAt', 'imgUrl'];
            const preparedData = Object.reject(this.category, ignoredKeys);
            const data = getFormData(preparedData);

            this.requestProcessing = true;
            return request({ method, url, data })
                .then(() => {
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });

                    this.requestProcessing = false;

                    this.closeEditDialog();
                    this.loadItems();
                })
                .catch(this.errorHandler);
        },
        errorHandler(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message: `brand.${message}`,
                    time: 2000
                });
            }
        },
        closeEditDialog() {
            this.resetData();
            this.editDialog = false;
        },
        openRemoveDialog(id) {
            this.idToRemove = id;
            this.removeDialog = true;
        },
        closeRemoveDialog() {
            this.idToRemove = null;
            this.removeDialog = false;
        },
        async remove() {
            if (this.requestProcessing) {
                return;
            }

            this.requestProcessing = true;
            return request({
                method: 'delete',
                url: `/delivery/category/${this.idToRemove}`
            }).then(res => {
                if (res.status === 200) {
                    this.idToRemove = null;
                    this.removeDialog = false;
                    this.requestProcessing = false;
                    this.loadItems();
                }
            }).catch(error => this.errorHandler(error));
        },
        resetData() {
            this.category = categoryInitialState();
        }
    }
};
</script>

<style lang="scss" scoped>
.active {
    color: green;
}

.inactive {
    color: red;
}
</style>
